import { useCallback, useEffect, useState } from 'react';

export const STORAGE_KEY = 'forecast-saved-spots';
const MAX_NAME_LEN = 60;
const ROUND_DP = 5;

const roundDp = (n) => {
    const f = Math.pow(10, ROUND_DP);
    return Math.round(n * f) / f;
};

const safeRead = () => {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
        return [];
    }
};

// Read saved spots straight from localStorage, outside the hook lifecycle.
// GlobalSearch lives at the app root and never unmounts, so it can't rely on a
// useSavedSpots instance to reflect spots saved later on the Forecast page — it
// calls this on each open to get a fresh snapshot.
export const readSavedSpots = safeRead;

const safeWrite = (spots) => {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(spots));
    } catch (_) {
        // Quota, private mode, etc. Fail silently — UI degrades gracefully.
    }
};

const normalizeName = (raw, fallback) => {
    if (typeof raw !== 'string') return fallback;
    const trimmed = raw.trim().slice(0, MAX_NAME_LEN);
    return trimmed.length ? trimmed : fallback;
};

const newId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const useSavedSpots = () => {
    const [spots, setSpots] = useState(safeRead);

    // Mirror to localStorage on every change.
    useEffect(() => { safeWrite(spots); }, [spots]);

    const addSpot = useCallback((name, lat, lng) => {
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        let created;
        setSpots((cur) => {
            created = {
                id: newId(),
                name: normalizeName(name, `Spot ${cur.length + 1}`),
                lat: roundDp(lat),
                lng: roundDp(lng),
                createdAt: Date.now(),
            };
            return [...cur, created];
        });
        return created;
    }, []);

    const removeSpot = useCallback((id) => {
        setSpots((cur) => cur.filter((s) => s.id !== id));
    }, []);

    const renameSpot = useCallback((id, name) => {
        setSpots((cur) => cur.map((s, idx) => (
            s.id === id ? { ...s, name: normalizeName(name, `Spot ${idx + 1}`) } : s
        )));
    }, []);

    const findSavedByCoords = useCallback((lat, lng) => {
        const rLat = roundDp(lat);
        const rLng = roundDp(lng);
        return spots.find((s) => s.lat === rLat && s.lng === rLng) || null;
    }, [spots]);

    return { spots, addSpot, removeSpot, renameSpot, findSavedByCoords };
};

export default useSavedSpots;
